import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useMemo} from 'react'

import {LABELS} from './constants/labels'
import {MilestoneError} from './MilestoneError'
import {MilestoneList} from './MilestoneList'
import {MilestonesActions} from './MilestonesActions'
import type {RepositoryResult} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'
import {MILESTONES_CONNECTION_PATH} from './utils/milestone-connection-operations'

export type RepositoryMilestonesInternalFragment = Pick<
  RepositoryResult,
  'id' | 'viewerCanCreateMilestones' | 'nameWithOwner' | 'milestones' | 'open' | 'closed'
>

type RepositoryMilestonesInternalProps = {
  repository: RepositoryMilestonesInternalFragment
  // Present only in the data-router app; enables the optimistic overlay + settle fold on the route query.
  queryKey?: RouteQueryKey
  onMutationSuccess?: (milestoneNumber: number) => void | Promise<void>
  onDeleteSuccess?: (milestoneNumber: number) => void | Promise<void>
}

export function RepositoryMilestonesInternal({
  repository: data,
  queryKey,
  onMutationSuccess,
  onDeleteSuccess,
}: RepositoryMilestonesInternalProps) {
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  // Overlay in-flight close/reopen ops onto the query data so the list and counts update instantly.
  //
  // Known limitation: ops are scoped by repo + connection path, not by tab/page/sort variant, so a close
  // still in flight when the user switches variant briefly leaks into the destination until it settles —
  // narrow and self-correcting. Tracked in github/github-ui#27757.
  const optimisticConnections = useMemo(
    () => [{ownerId: data.id, connectionPath: MILESTONES_CONNECTION_PATH}],
    [data.id],
  )
  const repository = useOptimisticData(data, {
    connections: optimisticConnections,
    enabled: queryKey != null,
  })

  return (
    <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}>
      <ErrorBoundary
        boundaryName="repository-milestones"
        fallback={<MilestoneError title={LABELS.milestonesError} message={LABELS.milestonesErrorMessage} />}
      >
        <MilestonesActions repository={repository} />
        <MilestoneList
          repository={repository}
          queryKey={queryKey}
          onMutationSuccess={onMutationSuccess}
          onDeleteSuccess={onDeleteSuccess}
        />
      </ErrorBoundary>
    </div>
  )
}
